export interface Player {
  id: number;
  name: string;
  position: string;
  team: string;
  jerseyNumber: string;
  height: number;
  weight: number;
  dateOfBirth: string;
  nationality: string;
  isActive: boolean;
  fantasyPointsPerGame: number;
  pointsPerGame: number;
  reboundsPerGame: number;
  assistsPerGame: number;
  stealsPerGame: number;
  blocksPerGame: number;
  turnoversPerGame: number;
  fieldGoalPercentage: number;
  threePointPercentage: number;
  freeThrowPercentage: number;
}

export interface Team {
  id: number;
  name: string;
  abbreviation: string;
  city: string;
  state: string;
  conference: string;
  arena: string;
  founded: string;
  isActive: boolean;
}

export interface FantasyTeam {
  id: number;
  name: string;
  ownerId: number;
  leagueId: number;
  logoUrl?: string;
  teamColors?: string;
  createdDate: string;
  isActive: boolean;
  owner: User;
  league: FantasyLeague;
  players: FantasyTeamPlayer[];
  games: FantasyTeamGame[];
  draftPicks: DraftPick[];
}

export interface User {
  id: number;
  username: string;
  email: string;
  firstName: string;
  lastName: string;
  profilePictureUrl?: string;
  dateOfBirth: string;
  phoneNumber?: string;
  location?: string;
  createdDate: string;
  lastLoginDate: string;
  isActive: boolean;
  emailVerified: boolean;
}

export interface FantasyLeague {
  id: number;
  name: string;
  description: string;
  ownerId: number;
  maxTeams: number;
  minTeams: number;
  scoringSystem: string;
  draftType: string;
  draftDate?: string;
  draftTimeLimit: number;
  status: string;
  createdDate: string;
  seasonStartDate?: string;
  seasonEndDate?: string;
  isPublic: boolean;
  password?: string;
}

export interface FantasyTeamPlayer {
  fantasyTeamId: number;
  playerId: number;
  isActive: boolean;
  acquiredDate: string;
  acquisitionType?: string;
  lineupPosition?: string | null; // PG, SG, SF, PF, C, BENCH1, BENCH2, BENCH3, INACTIVE
  player: Player;
}

export interface FantasyTeamGame {
  id: number;
  fantasyTeamId: number;
  totalFantasyPoints: number;
  roundStart: number;
  roundEnd: number;
  opponentFantasyTeamId?: number | null;
  opponentFantasyTeamScore?: number | null;
  fantasyTeam: FantasyTeam;
  opponentFantasyTeam?: FantasyTeam | null;
}

export interface FantasyTeamGameScheduleDto {
  id: number;
  fantasyTeamId: number;
  opponentFantasyTeamId?: number | null;
  fantasyTeamName: string;
  opponentFantasyTeamName?: string | null;
  roundStart: number;
  roundEnd: number;
  totalFantasyPoints: number;
  opponentFantasyTeamScore?: number | null;
}

export interface Game {
  id: number;
  homeTeamId: number;
  awayTeamId: number;
  date: string;
  homeTeamScore?: number;
  awayTeamScore?: number;
  isCompleted: boolean;
  homeTeam: Team;
  awayTeam: Team;
}

export interface PlayerGame {
  id: number;
  gameId: number;
  gameDate: string;
  homeTeam: string;
  awayTeam: string;
  homeTeamScore?: number;
  awayTeamScore?: number;
  status: string;
  venue: string;
  isStarter: boolean;
  minutesPlayed: number;
  points: number;
  rebounds: number;
  offensiveRebounds: number;
  defensiveRebounds: number;
  assists: number;
  steals: number;
  blocks: number;
  turnovers: number;
  fouls: number;
  fieldGoalsMade: number;
  fieldGoalsAttempted: number;
  threePointersMade: number;
  threePointersAttempted: number;
  freeThrowsMade: number;
  freeThrowsAttempted: number;
  fantasyPoints: number;
}

export interface DraftPick {
  id: number;
  fantasyTeamId: number;
  fantasyLeagueId: number;
  round: number;
  pickNumber: number;
  playerId?: number;
  fantasyTeam: FantasyTeam;
  fantasyLeague: FantasyLeague;
  player?: Player;
}

export interface LeagueStanding {
  teamId: number;
  teamName: string;
  ownerName: string;
  totalFantasyPoints: number;
  averageFantasyPointsPerGame: number;
  playerCount: number;
}

export interface LeagueStats {
  leagueId: number;
  leagueName: string;
  totalTeams: number;
  totalPlayers: number;
  averageTeamSize: number;
  topScoringTeam?: {
    teamName: string;
    ownerName: string;
    totalFantasyPoints: number;
  };
  leagueAverageFantasyPoints: number;
}

// Use environment variable for API base URL
// Defaults to local API in development, production API otherwise
const getApiBase = () => {
  // Check if environment variable is set
  if (process.env.REACT_APP_API_BASE_URL) {
    return process.env.REACT_APP_API_BASE_URL;
  }
  
  // Default to local API when running on localhost (development)
  // Use HTTP since HTTPS redirection is disabled in development
  if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
    return 'http://localhost:62671/api';
  }
  
  // Fallback to production API
  return 'https://downunderdynastyapi20250812024722-hbhqc2e6btb7g2av.australiaeast-01.azurewebsites.net/api';
};

const API_BASE_URL = getApiBase();

export const playerService = {
  async getAllPlayers(): Promise<Player[]> {
    const response = await fetch(`${API_BASE_URL}/players`);
    if (!response.ok) {
      throw new Error('Failed to fetch players');
    }
    return response.json();
  },

  async getOwnedPlayerIds(): Promise<number[]> {
    const response = await fetch(`${API_BASE_URL}/players/OwnedIds`);
    if (!response.ok) {
      throw new Error('Failed to fetch owned player IDs');
    }
    return response.json();
  },

  async getPlayerById(id: number): Promise<Player> {
    const response = await fetch(`${API_BASE_URL}/players/${id}`);
    if (!response.ok) {
      throw new Error('Failed to fetch player');
    }
    return response.json();
  },

  async getPlayerStats(id: number): Promise<{
    gamesPlayed: number;
    pointsPerGame: number;
    reboundsPerGame: number;
    assistsPerGame: number;
    stealsPerGame: number;
    blocksPerGame: number;
    turnoversPerGame: number;
    fantasyPointsPerGame: number;
    fieldGoalPercentage: number;
    threePointPercentage: number;
    freeThrowPercentage: number;
  }> {
    const response = await fetch(`${API_BASE_URL}/players/${id}/stats`);
    if (!response.ok) {
      throw new Error('Failed to fetch player statistics');
    }
    return response.json();
  },

  async getPlayerOwner(id: number): Promise<{
    isOwned: boolean;
    teamName: string | null;
    ownerName: string | null;
    fantasyTeamId: number | null;
  }> {
    const response = await fetch(`${API_BASE_URL}/players/${id}/owner`);
    if (!response.ok) {
      throw new Error('Failed to fetch player owner');
    }
    return response.json();
  },

  async getPlayerGames(id: number): Promise<PlayerGame[]> {
    const response = await fetch(`${API_BASE_URL}/players/${id}/Games`);
    if (!response.ok) {
      throw new Error('Failed to fetch player games');
    }
    return response.json();
  },

  async getTeams(): Promise<Team[]> {
    const response = await fetch(`${API_BASE_URL}/teams`);
    if (!response.ok) {
      throw new Error('Failed to fetch teams');
    }
    return response.json();
  },

  async getFantasyTeamsByUser(userId: number): Promise<FantasyTeam[]> {
    // Prefer lightweight endpoint; fallback to full endpoint if not available
    try {
      const basicResponse = await fetch(`${API_BASE_URL}/fantasyteams/BasicByUser/${userId}`);
      if (basicResponse.ok) {
        return basicResponse.json();
      }
      // If the basic endpoint is missing (404) fall back
      if (basicResponse.status !== 404) {
        const text = await basicResponse.text();
        throw new Error(text || 'Failed to fetch fantasy teams');
      }
    } catch (err) {
      console.warn('getFantasyTeamsByUser: basic endpoint failed, falling back to full', err);
    }

    const response = await fetch(`${API_BASE_URL}/fantasyteams/ByUser/${userId}`);
    if (!response.ok) {
      throw new Error('Failed to fetch fantasy teams');
    }
    return response.json();
  },

  async getFantasyLeaguesByUser(userId: number): Promise<FantasyLeague[]> {
    const response = await fetch(`${API_BASE_URL}/fantasyleagues/ByUser/${userId}`);
    if (!response.ok) {
      throw new Error('Failed to fetch fantasy leagues');
    }
    return response.json();
  },

  async getFantasyLeagues(): Promise<FantasyLeague[]> {
    const response = await fetch(`${API_BASE_URL}/fantasyleagues`);
    if (!response.ok) {
      throw new Error('Failed to fetch fantasy leagues');
    }
    return response.json();
  },

  async generateSchedule(leagueId: number): Promise<void> {
    const response = await fetch(`${API_BASE_URL}/fantasyleagues/${leagueId}/generate-schedule`, {
      method: 'POST'
    });
    if (!response.ok) {
      const text = await response.text();
      throw new Error(text || 'Failed to generate schedule');
    }
  },

  async deleteSchedule(leagueId: number): Promise<void> {
    const response = await fetch(`${API_BASE_URL}/fantasyleagues/${leagueId}/schedule`, {
      method: 'DELETE'
    });
    if (!response.ok) {
      const text = await response.text();
      throw new Error(text || 'Failed to delete schedule');
    }
  },

  async getFantasyTeam(id: number): Promise<FantasyTeam> {
    const response = await fetch(`${API_BASE_URL}/fantasyteams/${id}`);
    if (!response.ok) {
      throw new Error('Failed to fetch fantasy team');
    }
    return response.json();
  },

  async getUserByUsername(username: string): Promise<User> {
    const response = await fetch(`${API_BASE_URL}/users/ByUsername/${username}`);
    if (!response.ok) {
      throw new Error('Failed to fetch user');
    }
    return response.json();
  },

  // New methods for missing functionality
  async getFantasyTeamGamesByUser(userId: number): Promise<FantasyTeamGame[]> {
    const response = await fetch(`${API_BASE_URL}/fantasyteamgames/ByUser/${userId}`);
    if (!response.ok) {
      throw new Error('Failed to fetch fantasy team games for user');
    }
    return response.json();
  },

  async getFantasyTeamGamesByTeam(teamId: number): Promise<FantasyTeamGameScheduleDto[]> {
    const response = await fetch(`${API_BASE_URL}/fantasyteamgames/ByTeam/${teamId}`);
    if (!response.ok) {
      const text = await response.text();
      throw new Error(text || 'Failed to fetch fantasy team games');
    }
    return response.json();
  },

  async getUpcomingFantasyTeamGames(userId: number): Promise<FantasyTeamGame[]> {
    const response = await fetch(`${API_BASE_URL}/fantasyteamgames/Upcoming/${userId}`);
    if (!response.ok) {
      throw new Error('Failed to fetch upcoming games');
    }
    return response.json();
  },

  async getCurrentWeekFantasyTeamGames(userId: number): Promise<FantasyTeamGame[]> {
    const response = await fetch(`${API_BASE_URL}/fantasyteamgames/Current/${userId}`);
    if (!response.ok) {
      throw new Error('Failed to fetch current week games');
    }
    return response.json();
  },

  async getDraftPicksByUser(userId: number): Promise<DraftPick[]> {
    const response = await fetch(`${API_BASE_URL}/draftpicks/ByUser/${userId}`);
    if (!response.ok) {
      throw new Error('Failed to fetch draft picks');
    }
    return response.json();
  },

  async getUpcomingDraftPicks(userId: number): Promise<DraftPick[]> {
    const response = await fetch(`${API_BASE_URL}/draftpicks/Upcoming/${userId}`);
    if (!response.ok) {
      throw new Error('Failed to fetch upcoming draft picks');
    }
    return response.json();
  },

  async getLeagueStandings(leagueId: number): Promise<LeagueStanding[]> {
    const response = await fetch(`${API_BASE_URL}/leaguestandings/${leagueId}`);
    if (!response.ok) {
      throw new Error('Failed to fetch league standings');
    }
    return response.json();
  },

  async getUserLeagueStandings(userId: number): Promise<LeagueStanding[]> {
    const response = await fetch(`${API_BASE_URL}/leaguestandings/User/${userId}`);
    if (!response.ok) {
      throw new Error('Failed to fetch user league standings');
    }
    return response.json();
  },

  async getLeagueStats(leagueId: number): Promise<LeagueStats> {
    const response = await fetch(`${API_BASE_URL}/leaguestandings/Stats/${leagueId}`);
    if (!response.ok) {
      throw new Error('Failed to fetch league stats');
    }
    return response.json();
  },

  // Optimized methods to prevent database timeouts
  async getFantasyTeamsBasicByUser(userId: number): Promise<FantasyTeam[]> {
    // Only fetch basic team info without heavy navigation properties
    const response = await fetch(`${API_BASE_URL}/fantasyteams/BasicByUser/${userId}`);
    if (!response.ok) {
      throw new Error('Failed to fetch fantasy teams basic info');
    }
    return response.json();
  },

  async getFantasyTeamPlayers(teamId: number): Promise<FantasyTeamPlayer[]> {
    try {
      // Try the optimized endpoint first
      const response = await fetch(`${API_BASE_URL}/fantasyteams/${teamId}/players`);
      if (!response.ok) {
        throw new Error('Failed to fetch fantasy team players');
      }
      return response.json();
    } catch (error) {
      console.warn(`⚠️ Could not fetch team players from optimized endpoint for team ${teamId}, using empty array:`, error);
      // Return empty array as fallback - the data will be loaded from the main endpoint
      return [];
    }
  },

  async getFantasyTeamGames(teamId: number, limit: number = 10): Promise<FantasyTeamGame[]> {
    try {
      // Try the optimized endpoint first
      const response = await fetch(`${API_BASE_URL}/fantasyteams/${teamId}/games?limit=${limit}`);
      if (!response.ok) {
        throw new Error('Failed to fetch fantasy team games');
      }
      return response.json();
    } catch (error) {
      console.warn(`⚠️ Could not fetch team games from optimized endpoint for team ${teamId}, using empty array:`, error);
      // Return empty array as fallback - the data will be loaded from the main endpoint
      return [];
    }
  },

  async getFantasyTeamDraftPicks(teamId: number): Promise<DraftPick[]> {
    try {
      // Try the optimized endpoint first
      const response = await fetch(`${API_BASE_URL}/fantasyteams/${teamId}/draftpicks`);
      if (!response.ok) {
        throw new Error('Failed to fetch fantasy team draft picks');
      }
      return response.json();
    } catch (error) {
      console.warn(`⚠️ Could not fetch team draft picks from optimized endpoint for team ${teamId}, using empty array:`, error);
      // Return empty array as fallback - the data will be loaded from the main endpoint
      return [];
    }
  },

  // Enhanced method that loads data progressively
  async getFantasyTeamsWithProgressiveLoading(userId: number): Promise<FantasyTeam[]> {
    try {
      // Step 1: Load basic team info (fast)
      let basicTeams: FantasyTeam[];
      try {
        basicTeams = await this.getFantasyTeamsBasicByUser(userId);
      } catch (error) {
        console.warn('⚠️ Progressive loading: Basic endpoint failed, falling back to full endpoint:', error);
        // Fallback to existing endpoint if optimized one fails
        basicTeams = await this.getFantasyTeamsByUser(userId);
      }
      
      // Step 2: Load additional data for each team only when needed
      const enhancedTeams = await Promise.all(
        basicTeams.map(async (team) => {
          // Load players only if we're on a page that needs them
          if (window.location.pathname.includes('/lineup')) {
            try {
              team.players = await this.getFantasyTeamPlayers(team.id);
            } catch (error) {
              console.warn(`Failed to load players for team ${team.id}:`, error);
              team.players = [];
            }
          }
  
          // Load recent games only if we're on a page that needs them
          if (window.location.pathname.includes('/matchup') || window.location.pathname.includes('/stats')) {
            try {
              team.games = await this.getFantasyTeamGames(team.id, 5); // Only last 5 games
            } catch (error) {
              console.warn(`Failed to load games for team ${team.id}:`, error);
              team.games = [];
            }
          }
  
          // Load draft picks only if we're on the draft page
          if (window.location.pathname.includes('/draft')) {
            try {
              team.draftPicks = await this.getFantasyTeamDraftPicks(team.id);
            } catch (error) {
              console.warn(`Failed to load draft picks for team ${team.id}:`, error);
              team.draftPicks = [];
            }
          }
  
          return team;
        })
      );
  
      return enhancedTeams;
    } catch (error) {
      console.error('Error in progressive loading:', error);
      throw error;
    }
  },

  // Fallback method that uses the original endpoint but with timeout handling
  async getFantasyTeamsByUserWithTimeout(userId: number, timeoutMs: number = 30000): Promise<FantasyTeam[]> {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

    try {
      const response = await fetch(`${API_BASE_URL}/fantasyteams/ByUser/${userId}`, {
        signal: controller.signal
      });
      
      clearTimeout(timeoutId);
      
      if (!response.ok) {
        throw new Error('Failed to fetch fantasy teams');
      }
      
      return response.json();
    } catch (error: unknown) {
      clearTimeout(timeoutId);
      
      if (error instanceof Error && error.name === 'AbortError') {
        console.warn('Fantasy teams query timed out, falling back to progressive loading');
        return this.getFantasyTeamsWithProgressiveLoading(userId);
      }
      
      throw error;
    }
  },

  // Add player to fantasy team
  async addPlayerToTeam(teamId: number, playerId: number, acquisitionType: string = 'Free Agent'): Promise<FantasyTeamPlayer> {
    const response = await fetch(`${API_BASE_URL}/fantasyteams/${teamId}/players`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        playerId,
        acquisitionType
      })
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to add player to team: ${errorText}`);
    }

    return response.json();
  },

  // Remove player from fantasy team
  async removePlayerFromTeam(teamId: number, playerId: number): Promise<void> {
    const response = await fetch(`${API_BASE_URL}/fantasyteams/${teamId}/players/${playerId}`, {
      method: 'DELETE'
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to remove player from team: ${errorText}`);
    }
  },

  // Get available players (not currently on any team)
  async getAvailablePlayers(): Promise<Player[]> {
    const response = await fetch(`${API_BASE_URL}/players/available`);
    if (!response.ok) {
      // If the endpoint doesn't exist, fall back to getting all players
      return this.getAllPlayers();
    }
    return response.json();
  },

  // Get players currently on a specific fantasy team
  async getTeamPlayers(teamId: number): Promise<FantasyTeamPlayer[]> {
    const response = await fetch(`${API_BASE_URL}/fantasyteams/${teamId}/players`);
    if (!response.ok) {
      throw new Error('Failed to fetch team players');
    }
    return response.json();
  },

  // Update a player's lineup position on a fantasy team
  async updatePlayerLineupPosition(teamId: number, playerId: number, lineupPosition: string | null): Promise<FantasyTeamPlayer> {
    const response = await fetch(`${API_BASE_URL}/fantasyteams/${teamId}/players/${playerId}/lineup-position`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ lineupPosition }),
    });
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to update lineup position: ${errorText}`);
    }
    return response.json();
  },

  // Get all users (for user switcher)
  async getAllUsers(): Promise<User[]> {
    try {
      const response = await fetch(`${API_BASE_URL}/users`);
      if (!response.ok) {
        throw new Error(`Failed to fetch users: ${response.status}`);
      }
      return await response.json();
    } catch (error) {
      console.error('Error fetching users:', error);
      throw error;
    }
  },

  // Draft-related methods
  async initializeDraft(leagueId: number, rounds: number = 8, dropAllPlayers: boolean = false): Promise<{
    message: string;
    totalPicks: number;
    rounds: number;
    teamsCount: number;
  }> {
    const response = await fetch(`${API_BASE_URL}/draft/initialize/${leagueId}?rounds=${rounds}&dropAllPlayers=${dropAllPlayers}`, {
      method: 'POST'
    });
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to initialize draft: ${errorText}`);
    }
    return response.json();
  },

  async getDraftStatus(leagueId: number): Promise<{
    isInitialized: boolean;
    isComplete?: boolean;
    currentRound?: number;
    totalRounds?: number;
    currentPick?: {
      draftPickId: number;
      pickNumber: number;
      round: number;
      teamId: number;
      teamName: string;
      ownerId: number;
      ownerName: string;
    };
    completedPicksCount?: number;
    totalPicks?: number;
    picksRemaining?: number;
    message?: string;
  }> {
    const response = await fetch(`${API_BASE_URL}/draft/status/${leagueId}`);
    if (!response.ok) {
      throw new Error('Failed to fetch draft status');
    }
    return response.json();
  },

  async getDraftAvailablePlayers(leagueId: number): Promise<Player[]> {
    const response = await fetch(`${API_BASE_URL}/draft/available-players/${leagueId}`);
    if (!response.ok) {
      throw new Error('Failed to fetch available players');
    }
    return response.json();
  },

  async makeDraftPick(draftPickId: number, playerId: number, teamId: number): Promise<DraftPick> {
    const response = await fetch(`${API_BASE_URL}/draft/make-pick`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        draftPickId,
        playerId,
        teamId
      })
    });
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to make draft pick: ${errorText}`);
    }
    return response.json();
  },

  async getDraftPicks(leagueId: number): Promise<DraftPick[]> {
    const response = await fetch(`${API_BASE_URL}/draft/picks/${leagueId}`);
    if (!response.ok) {
      throw new Error('Failed to fetch draft picks');
    }
    return response.json();
  },

  async undoLastDraftPick(leagueId: number): Promise<{
    message: string;
    pickNumber: number;
    playerName: string;
    teamName: string;
  }> {
    const response = await fetch(`${API_BASE_URL}/draft/undo-last-pick/${leagueId}`, {
      method: 'POST'
    });
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to undo last pick: ${errorText}`);
    }
    return response.json();
  },

  async resetDraft(leagueId: number): Promise<{
    message: string;
    picksRemoved: number;
  }> {
    const response = await fetch(`${API_BASE_URL}/draft/reset/${leagueId}`, {
      method: 'DELETE'
    });
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to reset draft: ${errorText}`);
    }
    return response.json();
  },

  // Create a new fantasy team
  async createFantasyTeam(teamData: {
    name: string;
    ownerId: number;
    leagueId: number;
    logoUrl?: string;
    teamColors?: string;
  }): Promise<FantasyTeam> {
    const response = await fetch(`${API_BASE_URL}/fantasyteams`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        name: teamData.name,
        ownerId: teamData.ownerId,
        leagueId: teamData.leagueId,
        logoUrl: teamData.logoUrl || null,
        teamColors: teamData.teamColors || null,
        isActive: true
      })
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to create fantasy team: ${errorText}`);
    }

    return response.json();
  },

  // Delete a fantasy team
  async deleteFantasyTeam(teamId: number): Promise<void> {
    const response = await fetch(`${API_BASE_URL}/fantasyteams/${teamId}`, {
      method: 'DELETE'
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to delete fantasy team: ${errorText}`);
    }
  },

  // Create a user
  async createUser(userData: { username: string; email: string; firstName?: string; lastName?: string; }): Promise<User> {
    const response = await fetch(`${API_BASE_URL}/users`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        username: userData.username,
        email: userData.email,
        firstName: userData.firstName ?? '',
        lastName: userData.lastName ?? '',
        isActive: true,
        emailVerified: false
      })
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to create user: ${errorText}`);
    }

    return response.json();
  },

  // Delete a user by username
  async deleteUserByUsername(username: string): Promise<void> {
    const response = await fetch(`${API_BASE_URL}/users/ByUsername/${username}`, {
      method: 'DELETE'
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to delete user: ${errorText}`);
    }
  },

  async updateUserName(currentUsername: string, payload: { newUsername?: string; firstName?: string; lastName?: string; }): Promise<User> {
    const response = await fetch(`${API_BASE_URL}/users/ByUsername/${currentUsername}/name`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        newUsername: payload.newUsername,
        firstName: payload.firstName,
        lastName: payload.lastName
      })
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to update user: ${errorText}`);
    }

    return response.json();
  },

  async updateFantasyTeamName(teamId: number, name: string): Promise<FantasyTeam> {
    const response = await fetch(`${API_BASE_URL}/fantasyteams/${teamId}/name`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ name })
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to update team name: ${errorText}`);
    }

    return response.json();
  }
};
